import { ConfigApi, Configuration } from "@voice2mail/shared/v2m_api";
import { type FirebaseApp, initializeApp } from "firebase/app";
import {
  type Auth,
  getAuth,
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithPopup,
  signOut,
  type User,
} from "firebase/auth";

import { ENV_CONFIG, getEnvironment } from "../api/client";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
}

interface FirebaseInstances {
  app: FirebaseApp;
  auth: Auth;
  googleProvider: GoogleAuthProvider;
}

// ---------------------------------------------------------------------------
// Lazy / memoized init
// ---------------------------------------------------------------------------

/** Resolved once on first use; subsequent calls get the cached promise. */
let _initPromise: Promise<FirebaseInstances> | null = null;

/**
 * Try to fetch the firebase web config from the backend.
 * Returns null on any failure so the caller can fall back to build-time env.
 */
async function fetchRuntimeConfig(): Promise<FirebaseConfig | null> {
  try {
    const baseUrl = ENV_CONFIG[getEnvironment()].url;
    if (!baseUrl) return null;
    // Bootstrap call — runs before the rest of the app/client is wired, so it
    // builds its own minimal generated-client instance (no auth needed; the
    // config endpoint is public). The generated client targets the versioned
    // `/v1/...` path natively; the 5 s timeout is preserved via the request
    // init override.
    const configApi = new ConfigApi(new Configuration({ basePath: baseUrl }));
    const data = (await configApi.getFirebaseConfigV1ConfigFirebaseGet({
      signal: AbortSignal.timeout(5_000),
    })) as Partial<FirebaseConfig>;
    if (!data.apiKey) return null;
    return data as FirebaseConfig;
  } catch {
    // Network error, timeout, JSON parse failure — all non-fatal; fall back.
    return null;
  }
}

/** Build config from VITE_FIREBASE_* build-time env vars. */
function buildConfigFromEnv(): FirebaseConfig {
  return {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY ?? "",
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ?? "",
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID ?? "",
  };
}

/**
 * Initialise Firebase on first call; returns cached instances on subsequent calls.
 *
 * Strategy:
 *  1. Attempt GET /api/config/firebase on the current backend env (5 s timeout).
 *  2. On any failure / 404 fall back to build-time VITE_FIREBASE_* env vars.
 *  3. If neither source yields a non-empty apiKey, throw a clear error that
 *     surfaces in the UI — NOT as an uncaught module-evaluation exception.
 */
function ensureFirebase(): Promise<FirebaseInstances> {
  if (!_initPromise) {
    _initPromise = (async () => {
      let config = await fetchRuntimeConfig();

      if (!config) {
        config = buildConfigFromEnv();
      }

      if (!config.apiKey) {
        throw new Error(
          "Firebase config unavailable: no apiKey from runtime endpoint or build-time env. " +
            "Add VITE_FIREBASE_API_KEY to your .env.development file, or deploy the " +
            "runtime firebase-config backend endpoint."
        );
      }

      const app = initializeApp(config);
      const auth = getAuth(app);
      const googleProvider = new GoogleAuthProvider();
      return { app, auth, googleProvider };
    })();
  }
  return _initPromise;
}

// ---------------------------------------------------------------------------
// Public API — all async so callers await the lazy init transparently
// ---------------------------------------------------------------------------

export async function signInWithGoogle(): Promise<User> {
  const { auth, googleProvider } = await ensureFirebase();
  const result = await signInWithPopup(auth, googleProvider);
  return result.user;
}

export async function signOutUser(): Promise<void> {
  const { auth } = await ensureFirebase();
  return signOut(auth);
}

/**
 * Subscribe to auth state changes.
 *
 * Returns a Promise that resolves to the unsubscribe function once Firebase is
 * ready. Callers must handle the async gap (see useAuth.ts for the pattern).
 */
export async function onAuthChange(callback: (user: User | null) => void): Promise<() => void> {
  const { auth } = await ensureFirebase();
  return onAuthStateChanged(auth, callback);
}

export async function getIdToken(): Promise<string> {
  const { auth } = await ensureFirebase();
  const user = auth.currentUser;
  if (!user) throw new Error("Not authenticated");
  return user.getIdToken();
}
